import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { StatCard, PageHeader } from "@/components/Shell";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Vault, ArrowDownToLine, ArrowUpFromLine, Clock, Search, Lock } from "lucide-react";
import { Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return <div className="grid place-items-center py-32"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div>;

  const users = (data.users || []).filter(
    (u) => u.name.toLowerCase().includes(q.toLowerCase()) || u.email.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div>
      <PageHeader title="Admin Dashboard" desc="Full treasury overview across all users." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-users" icon={Users} label="Total Users" value={data.total_users} accent="cyan" />
        <StatCard testid="stat-vault" icon={Vault} label="Vault Balance" value={formatINR(data.total_vault_balance)} sub={`Reserved ${formatINR(data.total_reserved)}`} accent="emerald" />
        <StatCard testid="stat-deposits" icon={ArrowDownToLine} label="Total Deposits" value={formatINR(data.total_deposits)} accent="emerald" />
        <StatCard testid="stat-withdrawals" icon={ArrowUpFromLine} label="Total Payouts" value={formatINR(data.total_withdrawals)} accent="rose" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        <StatCard testid="stat-pending-dep" icon={Clock} label="Pending Deposits" value={data.pending_deposits_count} sub={formatINR(data.pending_deposits_amount)} accent="amber" />
        <StatCard testid="stat-pending-wd" icon={Lock} label="Pending Withdrawals" value={data.pending_withdrawals_count} sub={formatINR(data.pending_withdrawals_amount)} accent="amber" />
        <StatCard testid="stat-today" icon={Clock} label="Today's Transactions" value={data.today_transactions} sub="Indian Standard Time" accent="cyan" />
      </div>

      <div className="vp-card rounded-xl mt-6 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-white/5">
          <h3 className="font-semibold text-lg">User-wise Balance Overview</h3>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input data-testid="dashboard-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9 bg-slate-900/60 border-slate-700 h-10" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Available</TableHead>
                <TableHead className="text-slate-400">Reserved</TableHead>
                <TableHead className="text-slate-400">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">No users found.</TableCell></TableRow>
              ) : users.map((u) => (
                <TableRow key={u.id} className="border-white/5" data-testid={`dash-user-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </TableCell>
                  <TableCell className="font-mono text-emerald-300">{formatINR(u.balance)}</TableCell>
                  <TableCell className="font-mono text-amber-300">{formatINR(u.reserved)}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-mono ${u.status === "active" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>
                      {u.status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
